import { useCallback, useState } from "react";
import { classNameJoin, isNullOrEmpty } from "./util";
import playYouTube from "./assets/images/play_circle.svg";

export type SlideshowImage = {
  videoId?: string;
  src?: string;
  alt?: string;
  key?: number;
};

export type AdoptablePetDetailSlideshowProps = {
  images: SlideshowImage[];
  setOpen: (open: boolean) => void; // Accept setOpen as a prop
  open: boolean; // Accept open as a prop, if needed
};

export default function AdoptablePetDetailSlideshow({
  images,
  setOpen,
}: AdoptablePetDetailSlideshowProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleImageClick = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);
  return (
    <div className="flex flex-col gap-2 mb-8">
      <div className="grid place-items-center">
        <Slide setOpen={setOpen} image={images[currentIndex]} />
      </div>
      <div className="flex items-center justify-center gap-2">
        {images.map((image, index) => (
          <div
            className="w-20 h-20"
            onClick={() => void handleImageClick(index)}
          >
            <Thumb image={image} isActive={index === currentIndex} />
          </div>
        ))}
      </div>
    </div>
  );
}

function Slide({
  image,
  setOpen,
}: {
  image: SlideshowImage;
  setOpen: (open: boolean) => void;
}) {
  // function Slide({image}: { image: SlideshowImage }) {
  const videoId = (image.videoId ?? "").replace(/\r\n/g, "").trim();
  const video = (
    <iframe
      src={`https://www.youtube.com/embed/${videoId}`}
      className="rounded-md w-fill h-fill object-cover"
      style={{ width: "100%", height: "100%" }}
    />
  );

  return (
    <>
      {isNullOrEmpty(videoId) ? (
        <div
          className="relative rounded-md bg-cover bg-center bg-no-repeat w-[340px] h-[340px] sm:w-[480px] sm:h-[480px] overflow-hidden select-none"
          style={{ backgroundImage: `url(${image.src})` }}
        >
          <div
            onClick={() => setOpen(true)}
            className="absolute bottom-4 right-4 bg-primary p-2 rounded-full cursor-pointer hover:bg-primary-hover transition"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
              className="w-6 h-6"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="16" y1="16" x2="20" y2="20" />
            </svg>
          </div>
        </div>
      ) : (
        <div className="relative rounded-md w-[340px] h-[340px] sm:w-[480px] sm:h-[480px] overflow-hidden select-none">
          {video}
          <div
            onClick={() => setOpen(true)}
            className="absolute bottom-4 right-4 bg-black/60 p-2 rounded-full cursor-pointer hover:bg-black/80 transition"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
              className="w-6 h-6"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="16" y1="16" x2="20" y2="20" />
            </svg>
          </div>
        </div>
      )}
    </>
  );
}

function getYtPreviewImageUrl(videoId: string) {
  return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
}

function Thumb({
  image,
  isActive,
}: {
  image: SlideshowImage;
  isActive: boolean;
}) {
  const videoId = (image.videoId ?? "").replace(/\r\n/g, "").trim();
  const elemClass = classNameJoin(
    "w-full h-full rounded-md object-cover cursor-pointer",
    isActive ? "border-red-500 border-4" : undefined,
  );
  if (isNullOrEmpty(videoId)) {
    return <img className={elemClass} src={image.src} alt={image.alt} />;
  }
  return (
    <div
      className={elemClass}
      style={{
        cursor: "pointer",
        backgroundImage: `url(${getYtPreviewImageUrl(videoId)})`,
        backgroundSize: "cover",
      }}
    >
      <img src={playYouTube} />
    </div>
  );
}
